'use client';

import { ButtonWithLinkOrAction } from '@/components/buttons/Button.types';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import cn from 'classnames';
import { FC } from 'react';
import Link from './Link';

export type UseCaseHeroProps = {
  title: string;
  subtitle: string;
  description: string;
  links?: ButtonWithLinkOrAction[];
  media: React.ReactNode;
  logosTitle: string;
  companyLogos: React.ReactNode[] & { length: 3 | 4 };
  /**
   * Optional form rendered in the right column instead of `media`. When
   * provided, the form takes over the media slot so variants of this
   * hero can show a lead-capture form (e.g. PaidSearch demo form) in
   * place of the standard image/video.
   */
  form?: React.ReactNode;
}

const UseCaseHero: FC<UseCaseHeroProps> = ({
  title,
  subtitle,
  description,
  links = [],
  media,
  logosTitle,
  companyLogos = [],
  form,
}) => {
  const companyLogosGridColumns = companyLogos.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4';
  const { modalActionHandler } = useModalButtonActions();

  const header = (
    <div className="flex flex-col gap-2 md:gap-3">
      <p className="text-14/20 uppercase tracking-wide text-violet-800">{subtitle}</p>
      <h1 className="font-500 tracking-tight text-36/42 md:text-54/54 text-violet-900">{title}</h1>
      <p className="text-violet-800 text-17/24 max-w-80 text-pretty">{description}</p>
      {links.length > 0 && (
        <ul className="flex gap-1 md:gap-2">
          {links.map((link, index) => {
            return (
              <li key={`use-case-hero-link-${link.title?.replace(/\s+/g, '-') ?? index}`}>
                {link.href.length ? (
                  <Link
                    className={cn('block button button-size-medium', {
                      'button-primary-violet': link.type === 'primary',
                      'button-secondary-violet': link.type === 'secondary',
                    })}
                    href={link.href}
                  >
                    {link.title}
                  </Link>
                ) : (
                  <button
                    className={cn('block button button-size-medium', {
                      'button-primary-violet': link.type === 'primary',
                      'button-secondary-violet': link.type === 'secondary',
                    })}
                    onClick={() => {
                      modalActionHandler(link.action);
                    }}
                  >
                    {link.title}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const logoFooter = (
    <div className="flex flex-col gap-4">
      <h2 className="text-25/30 font-500 text-violet-900">{logosTitle}</h2>
      <div className={`grid grid-cols-2 gap-x-gutter-mobile gap-y-4 ${companyLogosGridColumns}`}>
        {companyLogos}
      </div>
    </div>
  );

  return (
    <div className='my-2 md:my-8 md:px-container-desktop'>
      <div className="mx-auto max-w-grid">
        <div className="bg-violet-200 px-container-mobile py-5 md:p-8 md:rounded-20 md:-mx-8">
          <div className='grid lg:grid-cols-12 gap-y-4 gap-x-gutter-mobile md:gap-x-gutter-desktop'>
            <div className='lg:col-span-5'>
              <div className='flex flex-col gap-8'>
                {header}
                <div className='hidden lg:block'>
                  {logoFooter}
                </div>
              </div>
            </div>
            <div className='lg:col-span-6 lg:col-start-7'>
              <div className='flex flex-col gap-8'>
                {form ? (
                  <div className='rounded-10 overflow-hidden grid'>
                    {form}
                  </div>
                ) : (
                  media
                )}
                <div className='lg:hidden'>
                  {logoFooter}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseCaseHero;
